import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button, IconButton, Skeleton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HomeScreenImageItem } from "../../api-data/service-provider/home-screen-images";
import { useBannerEffect } from "../../new-components/banner-effects/BannerEffect";
import { useLocation } from "../../context/NewLocationContext";
import { buildSearchUrl } from "../../utils/urlHelpers";

interface Props {
  images: HomeScreenImageItem[];
  isMobile: boolean;
  loading?: boolean;
}

const AUTO_SCROLL_TIME = 10000;

const HomeBannerCarousel: React.FC<Props> = ({
  images,
  isMobile,
  loading = false,
}) => {
  const [index, setIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const { selectedCity, selectedLocality } = useLocation();

  const total = images.length;

  // ── Active slide + effect ──────────────────────────────────────────────────
  // Hook must be called unconditionally at the top level (rules of hooks).
  // Falls back to a no-op result when images is empty so the hook is always
  // called the same number of times regardless of loading state.
  const activeBanner: HomeScreenImageItem | null = images[index] ?? null;
  const { imgSx, overlay } = useBannerEffect(
    activeBanner?.effect ?? "none",
    activeBanner?.effectConfig
  );

  // Reset loaded state whenever the active slide changes so the effect never
  // fires before the new image has fully painted.
  useEffect(() => {
    setImageLoaded(false);
  }, [index]);

  useEffect(() => {
    if (total <= 1) return;

    timerRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, AUTO_SCROLL_TIME);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [total]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, AUTO_SCROLL_TIME);
  };

  const prev = () => {
    setIndex((i) => (i - 1 + total) % total);
    resetTimer();
  };

  const next = () => {
    setIndex((i) => (i + 1) % total);
    resetTimer();
  };

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100%",
          height: isMobile ? "240px" : "40vh",
          borderRadius: 1,
        }}
      />
    );
  }

  if (!images.length) return null;

  // Resolve localized fields — `localized` map is populated by the backend
  // translation applier when the user's locale differs from the default.
  const displayTitle    = activeBanner?.localized?.title    ?? activeBanner?.title;
  const displaySubtitle = activeBanner?.localized?.subtitle ?? activeBanner?.subtitle;
  const displayCtaLabel = activeBanner?.localized?.ctaLabel ?? activeBanner?.ctaLabel;

  // Always show the overlay — at minimum a fallback CTA button is rendered.
  const hasTextOverlay = true;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: isMobile ? 0 : "24px",
        overflow: "hidden",
        "& .carousel-arrow": {
          opacity: isMobile ? 1 : 0,
          transition: "opacity 0.25s ease",
        },
        "&:hover .carousel-arrow": {
          opacity: 1,
        },
      }}
    >
      {/* ── Banner image ─────────────────────────────────────────────────────
          imgSx (ken-burns etc.) and overlay are only applied/rendered after the
          image fires onLoad — so the effect never runs on a blank/loading frame. */}
      <Box
        component="img"
        src={activeBanner?.imageUrl || ""}
        alt={activeBanner?.title ?? "Home Banner"}
        onLoad={handleImageLoad}
        sx={{
          width: "100%",
          height: isMobile ? "240px" : "100%",
          minHeight: isMobile ? undefined : "380px",
          objectFit: "cover",
          display: "block",
          ...(imageLoaded ? (imgSx ?? {}) : {}),
        }}
      />

      {/* ── Effect overlay (light-sweep / droplets / etc.) ── */}
      {imageLoaded && overlay}

      {/* ── Text + CTA scrim overlay ───────────────────────────────────────── */}
      {/* Anchored to the left side of the banner, vertically centred in the
          upper 55% — keeps it clear of the search card that overlaps the
          bottom ~30% of the banner via negative margin in the page layout. */}
      {hasTextOverlay && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            // dark gradient scrim from bottom — matches image 2
            background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0) 70%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            px: isMobile ? 2 : 3,
            // On mobile the HomeSearchDialog card overlaps the bottom ~72px of
            // the banner (mt:-8 = 64px + card padding). Push the CTA up so it
            // sits above the overlapping card and stays visible.
            pb: isMobile ? 10 : 3,
            gap: 0.5,
            pointerEvents: "none",
          }}
        >
          {displayTitle && (() => {
            const cfg    = activeBanner?.effectConfig ?? {};
            const color  = typeof cfg.titleFontColor === "string" ? cfg.titleFontColor : "common.white";
            const styles: string[] = Array.isArray(cfg.titleFontStyles) ? cfg.titleFontStyles : [];
            return (
              <Box
                component="span"
                sx={{
                  color,
                  fontWeight:     700,
                  fontStyle:      styles.includes("italic")    ? "italic"    : "normal",
                  textDecoration: styles.includes("underline") ? "underline" : "none",
                  fontSize:       isMobile ? 18 : 24,
                  lineHeight: 1.2,
                  textShadow: "0 1px 4px rgba(0,0,0,0.4)",
                }}
              >
                {displayTitle}
              </Box>
            );
          })()}

          {displaySubtitle && (() => {
            const cfg    = activeBanner?.effectConfig ?? {};
            const color  = typeof cfg.subtitleFontColor === "string" ? cfg.subtitleFontColor : "rgba(255,255,255,0.9)";
            const styles: string[] = Array.isArray(cfg.subtitleFontStyles) ? cfg.subtitleFontStyles : [];
            return (
              <Box
                component="span"
                sx={{
                  color,
                  fontWeight:     styles.includes("bold")      ? 700        : 400,
                  fontStyle:      styles.includes("italic")    ? "italic"   : "normal",
                  textDecoration: styles.includes("underline") ? "underline": "none",
                  fontSize:       isMobile ? 12.5 : 14,
                  lineHeight: 1.4,
                  textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              >
                {displaySubtitle}
              </Box>
            );
          })()}

          {/* CTA — rendered when the banner has a linkType set.
              INTERNAL → React Router <Link> for SPA navigation.
              EXTERNAL → plain <a href> opened in a new tab.
              KEYWORD  → buildSearchUrl with the user's current city injected. */}
          {activeBanner?.linkType && (() => {
            const label = displayCtaLabel || t("home.bannerPlaceholderCta");
            const type  = activeBanner.linkType;

            // Resolve the URL based on type.
            // linkKeyword is stored inside effectConfig (no extra backend field needed).
            let url: string | null = null;
            if (type === "KEYWORD" && activeBanner.effectConfig?.linkKeyword) {
              url = buildSearchUrl(selectedCity, selectedLocality, activeBanner.effectConfig.linkKeyword);
            } else if ((type === "INTERNAL" || type === "EXTERNAL") && activeBanner.linkUrl) {
              url = activeBanner.linkUrl;
            }

            if (!url) return null;

            const btnSx = {
              mt: 1,
              pointerEvents: "auto" as const,
              fontWeight: 700,
              borderRadius: "100px",
              px: isMobile ? 2 : 2.5,
            };

            return type === "EXTERNAL" ? (
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? "small" : "medium"}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
                sx={btnSx}
              >
                {label}
              </Button>
            ) : (
              // INTERNAL and KEYWORD both use client-side navigation
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? "small" : "medium"}
                component={Link}
                to={url}
                endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
                sx={btnSx}
              >
                {label}
              </Button>
            );
          })()}
        </Box>
      )}

      {/* ── Prev / Next arrows ───────────────────────────────────────────────── */}
      {total > 1 && (
        <IconButton
          className="carousel-arrow"
          onClick={prev}
          sx={{
            position: "absolute",
            top: "50%",
            left: 4,
            transform: "translateY(-50%)",
            color: "rgba(255,255,255,0.6)",
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      {total > 1 && (
        <IconButton
          className="carousel-arrow"
          onClick={next}
          sx={{
            position: "absolute",
            top: "50%",
            right: 4,
            transform: "translateY(-50%)",
            color: "rgba(255,255,255,0.6)",
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* ── Dot indicators ───────────────────────────────────────────────────── */}
      {total > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
          }}
        >
          {images.map((_, i) => (
            <Box
              key={i}
              onClick={() => {
                setIndex(i);
                resetTimer();
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                cursor: "pointer",
                backgroundColor:
                  i === index
                    ? "rgba(255,255,255,0.9)"
                    : "rgba(255,255,255,0.35)",
              }}
            />
          ))}
        </Box>
      )}

    </Box>
  );
};

export default HomeBannerCarousel;
